/**
 * 🆕 JAN 31, 2026: Smart Phase Grouping for Repeating Segments
 * 🔄 MAR 12, 2026: Added alternating A-B-A-B pattern detection (strides + recovery)
 *
 * Groups identical consecutive phases (strides, intervals) for cleaner UI display.
 * Also detects alternating work/recovery patterns and collapses them into interval blocks.
 * Backend structure remains unchanged - this is purely a display optimization.
 *
 * Examples:
 * - 4 identical strides → "Strides (4x)" with one cycle shown
 * - 6 stride + recovery pairs → "Strides (6×)" interval block with cycle preview
 * - 3 identical 800m intervals with recovery → "10K Pace Intervals (3x)" with one cycle
 * - Progressive intervals (different paces) → Keep separate (not identical)
 */

import { cleanPaceString, phasePaceTarget, type PhasePaceTarget } from './phasePaceTarget';

interface Phase {
  phase_name?: string;
  name?: string;
  phase_type?: string;
  duration_minutes?: number;
  duration_seconds?: number;
  distance_meters?: number;
  distance_km?: number;
  target_pace_min_per_km?: string;
  target_pace_max_per_km?: string;
  target_pace?: string;
  /** 🏊🚣 D6: swim (per-100 m) / row (per-500 m) targets — read via phasePaceTarget. */
  prescription_axis?: string;
  target_pace_min_per_100m?: string | null;
  target_pace_max_per_100m?: string | null;
  target_pace_min_per_500m?: string | null;
  target_pace_max_per_500m?: string | null;
  target_hr_min?: number;
  target_hr_max?: number;
  hr_zone?: number;
  /**
   * Canonical zone number consumed by PhaseRow (WorkoutDetail) and the timer.
   *
   * 🛠️ 2026-07-28 — this field was MISSING from the local interface while
   * `expandPhasesForTimer` writes it on every generated recovery phase (see the
   * `recovery_target_hr_zone` carry below). That mismatch was a real TS error in
   * production code, and it is why `phaseExpansion.test.ts` + `hrZoneSummary.test.ts`
   * sit in jest.config.js `testPathIgnorePatterns` (entries #4 and #4b, whose
   * comments say "Fix phaseGrouping.ts before re-enabling"). Declaring the field
   * is the correct fix — the value is genuinely read downstream.
   */
  target_hr_zone?: number;
  instructions?: string;
  description?: string;
  coaching_cues?: string[];
  key_focus_points?: string[];
  repeat_count?: number;
  rest_after_seconds?: number;
  exercises_json?: any[];
  target_cadence_rpm?: number;
  recovery_hr_target?: number;
  // 🆕 APR 2026: Rich recovery fields for compact interval format
  recovery_phase_name?: string | null;
  recovery_instructions?: string | null;
  recovery_target_hr_zone?: number | null;
  phase_order?: number;
  /** Expansion metadata — only present on phases produced by expandPhasesForTimer().
   *  Never serialized to backend. Consumers should check `phase.expansion` before reading. */
  expansion?: ExpansionMeta;
}

export interface ExpansionMeta {
  rep: number;          // Current rep (1-based), e.g. 3 for "Stride 3/8"
  repTotal: number;     // Total reps, e.g. 8
  isRecovery: boolean;  // True for generated inter-rep recovery phases
  blockName: string;    // Original phase name before expansion, e.g. "Strides"
}

export interface GroupedPhase {
  type: 'single' | 'group' | 'interval_block';
  phaseName: string;
  repeatCount?: number;
  phases: Phase[];  // For groups: all identical phases, For single: array with one phase
  originalIndices: number[];  // Track original indices for rendering
  /** For interval_block: representative [work, recovery] pair */
  cyclePair?: [Phase, Phase];
}

/**
 * 🏊🚣 D6 (2026-09-21) — do two phases prescribe the same swim / row target?
 *
 * A swim's target lives in the per-100 m slots and a row's in the per-500 m
 * slots; the per-km check in arePhasesSimilar never reads them. PhaseRow now
 * PRINTS that target (via phasePaceTarget), and a grouped row renders only its
 * FIRST phase. So a 10-minute warm-up at 2:10–2:20/100m followed by a
 * 10-minute main set at 1:50–1:55/100m collapsed into one "2×" row and the
 * main set's target vanished from the phase list, while the targets panel on
 * the same screen listed both.
 *
 * Compares the family each phase actually renders:
 *   - neither phase targets per-100 m / per-500 m ⇒ true, so run and bike
 *     grouping is decided by the legacy per-km check alone (byte-identical);
 *   - one side targets per-100 m / per-500 m and the other targets nothing or
 *     another unit ⇒ false (the rendered target would differ);
 *   - same unit ⇒ each bound must be present on both or absent on both, and
 *     within the per-km check's 5 s/km scaled to the unit — 0.5 s per 100 m,
 *     2.5 s per 500 m. Stored paces are whole seconds, so any per-100 m
 *     difference splits (a descending 1:50 / 1:48 / 1:46 set stays separate).
 */
const SWIM_ROW_TOLERANCE_SEC: Record<'per_100m' | 'per_500m', number> = {
  per_100m: 5 * (100 / 1000),
  per_500m: 5 * (500 / 1000),
};

function paceStringSeconds(value: unknown): number | null {
  const s = cleanPaceString(value);
  if (!s) return null;
  const [m, sec] = s.split(':');
  return Number(m) * 60 + Number(sec);
}

function swimRowTargetsMatch(phase1: Phase, phase2: Phase): boolean {
  const t1 = phasePaceTarget(phase1);
  const t2 = phasePaceTarget(phase2);
  const isSwimRow = (t: PhasePaceTarget | null): boolean => !!t && t.unit !== 'per_km';
  if (!isSwimRow(t1) && !isSwimRow(t2)) return true;
  if (!t1 || !t2 || t1.unit !== t2.unit || t1.unit === 'per_km') return false;
  const tolerance = SWIM_ROW_TOLERANCE_SEC[t1.unit];
  const bounds: Array<[unknown, unknown]> = [
    [t1.lo, t2.lo],
    [t1.hi, t2.hi],
  ];
  for (const [a, b] of bounds) {
    const aSec = paceStringSeconds(a);
    const bSec = paceStringSeconds(b);
    if (aSec === null && bSec === null) continue;
    if (aSec === null || bSec === null) return false;
    if (Math.abs(aSec - bSec) > tolerance) return false;
  }
  return true;
}

function nameWithoutCount(phase: Phase): string {
  return String(phase.phase_name || phase.name || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word && !/^\d+$/.test(word))
    .join(' ');
}

/**
 * Determines if two phases are identical for grouping purposes
 */
function arePhasesSimilar(phase1: Phase, phase2: Phase): boolean {
  if (nameWithoutCount(phase1) !== nameWithoutCount(phase2)) return false;

  // Two steps done on different things are never "the same step repeated"
  // (endurance PR-8f): a 1 km run and a 1000 m SkiErg, or a sled push and a
  // sandbag lunge with similar target times, would otherwise fold into one
  // "N×" row and hide what the athlete actually does. Only phases that BOTH
  // name a modality / exercises are compared; every other phase is unchanged.
  const modality = (p: Phase): string | null => {
    const m = (p as { execution_modality?: unknown }).execution_modality;
    return typeof m === 'string' && m ? m : null;
  };
  const m1 = modality(phase1);
  const m2 = modality(phase2);
  if (m1 && m2 && m1 !== m2) return false;
  const exerciseNames = (p: Phase): string | null => {
    const list = p.exercises_json;
    if (!Array.isArray(list) || list.length === 0) return null;
    return list.map((e: { name?: unknown }) => String(e?.name ?? '').trim().toLowerCase()).join('|');
  };
  const e1 = exerciseNames(phase1);
  const e2 = exerciseNames(phase2);
  if ((e1 || e2) && e1 !== e2) return false;

  // Check if both have similar distance (within 10%)
  const dist1 = phase1.distance_km || (phase1.distance_meters ? phase1.distance_meters / 1000 : 0);
  const dist2 = phase2.distance_km || (phase2.distance_meters ? phase2.distance_meters / 1000 : 0);

  if (dist1 > 0 && dist2 > 0) {
    const distDiff = Math.abs(dist1 - dist2) / Math.max(dist1, dist2);
    if (distDiff > 0.1) return false; // >10% difference = not similar
  }

  // Check if both have similar duration (within 10%)
  const dur1 = phase1.duration_seconds || (phase1.duration_minutes ? phase1.duration_minutes * 60 : 0);
  const dur2 = phase2.duration_seconds || (phase2.duration_minutes ? phase2.duration_minutes * 60 : 0);

  if (dur1 > 0 && dur2 > 0) {
    const durDiff = Math.abs(dur1 - dur2) / Math.max(dur1, dur2);
    if (durDiff > 0.1) return false; // >10% difference = not similar
  }

  // Check if both have similar pace (within 5 seconds/km)
  const pace1 = phase1.target_pace_min_per_km || phase1.target_pace;
  const pace2 = phase2.target_pace_min_per_km || phase2.target_pace;

  if (pace1 && pace2) {
    // Convert pace strings to seconds (e.g., "5:30" → 330)
    const paceToSeconds = (p: string): number => {
      const parts = p.split(':');
      if (parts.length === 2) {
        return parseInt(parts[0]) * 60 + parseInt(parts[1]);
      }
      return 0;
    };

    const pace1Sec = paceToSeconds(pace1);
    const pace2Sec = paceToSeconds(pace2);

    if (Math.abs(pace1Sec - pace2Sec) > 5) return false; // >5s/km difference = not similar
  }

  // 🏊🚣 D6 (2026-09-21): the per-100 m / per-500 m targets. See
  // swimRowTargetsMatch — a no-op unless a phase targets one of those units.
  if (!swimRowTargetsMatch(phase1, phase2)) return false;

  // Check if both have similar HR targets (within 5 bpm)
  if (phase1.target_hr_min && phase2.target_hr_min) {
    if (Math.abs(phase1.target_hr_min - phase2.target_hr_min) > 5) return false;
  }

  if (phase1.target_hr_max && phase2.target_hr_max) {
    if (Math.abs(phase1.target_hr_max - phase2.target_hr_max) > 5) return false;
  }

  // Check phase type (strides are usually same type)
  const type1 = (phase1.phase_name || phase1.name || '').toLowerCase();
  const type2 = (phase2.phase_name || phase2.name || '').toLowerCase();

  // If both contain "stride", they're similar
  if (type1.includes('stride') && type2.includes('stride')) return true;

  // If both contain "interval", they're similar
  if (type1.includes('interval') && type2.includes('interval')) return true;

  // If both contain specific pattern (e.g., "800m", "400m")
  // Allow grouping if they're part of interval work/recovery pattern

  // All checks passed - phases are similar
  return true;
}

/**
 * Determines a group name from a set of similar phases
 */
function getGroupName(phases: Phase[]): string {
  if (phases.length === 0) return 'Run';

  const firstPhaseName = (phases[0].phase_name || phases[0].name || '').toLowerCase();

  // Handle strides
  if (firstPhaseName.includes('stride')) {
    return 'Strides';
  }

  // Handle sprint intervals
  if (firstPhaseName.includes('sprint')) {
    return 'Sprint Intervals';
  }

  // Handle specific interval patterns (800m, 400m, 1K, etc.)
  const distanceMatch = firstPhaseName.match(/(\d+)(m|k|km|meter|kilometer)/i);
  if (distanceMatch) {
    const distance = distanceMatch[1] + distanceMatch[2].replace('meter', 'm').replace('kilometer', 'km');

    // Check if this is 10K pace work
    if (firstPhaseName.includes('10k') || firstPhaseName.includes('goal')) {
      return `${distance} 10K Pace Intervals`;
    }

    // Check if this is threshold/tempo work
    if (firstPhaseName.includes('threshold') || firstPhaseName.includes('tempo')) {
      return `${distance} Threshold Intervals`;
    }

    // Generic interval name
    return `${distance} Intervals`;
  }

  // Handle generic intervals
  if (firstPhaseName.includes('interval')) {
    // Try to extract pace descriptor
    if (firstPhaseName.includes('10k')) return '10K Pace Intervals';
    if (firstPhaseName.includes('threshold')) return 'Threshold Intervals';
    if (firstPhaseName.includes('tempo')) return 'Tempo Intervals';
    return 'Intervals';
  }

  // Fallback: Use the phase name with number stripped
  return (phases[0].phase_name || phases[0].name || 'Run')
    .replace(/\s*\d+\s*$/, '')  // Remove trailing number
    .replace(/^\d+\s*/, '');     // Remove leading number
}

/**
 * Detects alternating A-B-A-B patterns in a list of single GroupedPhases.
 * Returns a new list with interval_block entries replacing the alternating sequences.
 *
 * Example: [warmup, easy, stride1, recovery1, stride2, recovery2, ..., cooldown]
 * → [warmup, easy, {interval_block: Strides 6×}, cooldown]
 */
function collapseAlternatingPairs(initial: GroupedPhase[]): GroupedPhase[] {
  const result: GroupedPhase[] = [];
  let i = 0;

  while (i < initial.length) {
    // Only try to detect alternating patterns among consecutive 'single' phases
    if (initial[i].type !== 'single' || i + 3 >= initial.length) {
      result.push(initial[i]);
      i++;
      continue;
    }

    // Check if [i, i+1] forms a pair that repeats at [i+2, i+3]
    const phaseA = initial[i].phases[0];
    const phaseB = initial[i + 1].phases[0];

    // A and B must be different from each other (work vs recovery)
    if (initial[i + 1].type !== 'single' || arePhasesSimilar(phaseA, phaseB)) {
      result.push(initial[i]);
      i++;
      continue;
    }

    // Look ahead for repeating A-B pairs
    let cycles = 1; // we have one A-B pair
    let j = i + 2;

    while (
      j + 1 < initial.length &&
      initial[j].type === 'single' &&
      initial[j + 1].type === 'single' &&
      arePhasesSimilar(phaseA, initial[j].phases[0]) &&
      arePhasesSimilar(phaseB, initial[j + 1].phases[0])
    ) {
      cycles++;
      j += 2;
    }

    // Also check for a trailing A without B (e.g., stride without final recovery)
    // — except on a HYROX course (PR-8f): there the "trailing" run is the start of
    // the next pair whenever another course step follows it, and absorbing it
    // would fold run, sled push, run into "2×" while a different station waits.
    const courseStep = (k: number): boolean =>
      k < initial.length && typeof (initial[k].phases[0] as { race_segment_order?: unknown })?.race_segment_order === 'number';
    if (
      j < initial.length &&
      initial[j].type === 'single' &&
      arePhasesSimilar(phaseA, initial[j].phases[0]) &&
      !(courseStep(j) && courseStep(j + 1))
    ) {
      // Include the trailing work phase in the block
      cycles++;
      j++;
    }

    // Need at least 2 cycles (4 phases) to form a block
    if (cycles >= 2) {
      const allPhases: Phase[] = [];
      const allIndices: number[] = [];
      for (let k = i; k < j; k++) {
        allPhases.push(...initial[k].phases);
        allIndices.push(...initial[k].originalIndices);
      }

      const groupName = getGroupName([phaseA]);

      result.push({
        type: 'interval_block',
        phaseName: groupName,
        repeatCount: cycles,
        phases: allPhases,
        originalIndices: allIndices,
        cyclePair: [phaseA, phaseB],
      });
      i = j;
    } else {
      result.push(initial[i]);
      i++;
    }
  }

  return result;
}

/**
 * Groups consecutive identical phases for cleaner UI display,
 * then collapses alternating work/recovery patterns into interval blocks.
 *
 * Example Input:
 * [warmup, easy run, stride 1, recovery, stride 2, recovery, stride 3, recovery, cooldown]
 *
 * Example Output:
 * [
 *   {type: 'single', phaseName: 'Warmup', ...},
 *   {type: 'single', phaseName: 'Easy Run', ...},
 *   {type: 'interval_block', phaseName: 'Strides', repeatCount: 3, cyclePair: [stride, recovery], ...},
 *   {type: 'single', phaseName: 'Cooldown', ...}
 * ]
 */
export function groupRepeatingPhases(phases: Phase[]): GroupedPhase[] {
  if (!Array.isArray(phases) || phases.length === 0) {
    return [];
  }

  // Pass 1: Group consecutive identical phases
  const grouped: GroupedPhase[] = [];
  let i = 0;

  while (i < phases.length) {
    const currentPhase = phases[i];

    // Check if this is a repeating segment (look ahead for similar phases)
    const similarPhases: Phase[] = [currentPhase];
    const originalIndices: number[] = [i];
    let j = i + 1;

    // Look for consecutive similar phases
    while (j < phases.length && arePhasesSimilar(currentPhase, phases[j])) {
      similarPhases.push(phases[j]);
      originalIndices.push(j);
      j++;
    }

    // If we found 2+ similar phases, create a group
    if (similarPhases.length >= 2) {
      const groupName = getGroupName(similarPhases);

      grouped.push({
        type: 'group',
        phaseName: groupName,
        repeatCount: similarPhases.length,
        phases: similarPhases,
        originalIndices
      });

      i = j; // Skip past all grouped phases
    } else {
      // Single phase (not part of a repeating group)
      // Propagate phase.repeat_count so downstream components don't need fallback logic
      grouped.push({
        type: 'single',
        phaseName: currentPhase.phase_name || currentPhase.name ||
                   (currentPhase.phase_type === 'warmup' ? 'Warm-up' :
                    currentPhase.phase_type === 'cooldown' ? 'Cooldown' :
                    currentPhase.phase_type === 'recovery' ? 'Recovery' :
                    'Run'),
        repeatCount: currentPhase.repeat_count,
        phases: [currentPhase],
        originalIndices: [i]
      });

      i++;
    }
  }

  // Pass 2: Collapse alternating A-B-A-B patterns into interval blocks
  return collapseAlternatingPairs(grouped);
}

/**
 * Helper to format the repeat count display
 */
export function formatRepeatCount(count: number): string {
  return `${count}x`;
}

/**
 * 🆕 APR 7, 2026: Expand phases with repeat_count for timer execution.
 *
 * The backend sends compact phases: a single interval phase with repeat_count: 8
 * and rest_after_seconds: 80 means "run this phase 8 times with 80s recovery between reps."
 *
 * The timer iterates phases linearly, so this function unrolls compact phases into
 * individual work + recovery entries the timer can step through one by one.
 *
 * Example:
 *   Input:  [warmup, {stride 20s, repeat_count:8, rest_after:80s}, cooldown]
 *   Output: [warmup, stride, recovery, stride, recovery, ..., stride, cooldown]
 *           (8 stride phases with 7 recovery phases between them)
 */
/**
 * Ghost-phase filter, EXPORTED and run UNCONDITIONALLY by the timer's load
 * path (Mission 35b MIRROR-DRIFT). Pre-split, this filter lived inside
 * expandPhasesForTimer and therefore only ran when some phase carried
 * repeat_count > 1 — a plan with no repeats shipped its ghost phases
 * straight into the timer. The backend's _expand_prescribed_phases ports
 * this predicate VERBATIM (in_app_activity_bridge.py) — the two must stay
 * matched or the positional zip misaligns; the cross-language parity gate
 * pins them.
 *
 * Keeps phases with: duration, distance, exercises, OR text content
 * (instructions/coaching_cues — valid for RPE-based "until ready" phases).
 * 🛡️ 2026-07-28 — type guards on the text/list fields. `p.instructions.trim()`
 * threw when the planner emitted an array of instruction lines instead of a
 * string, and `.length` on a non-array `coaching_cues`/`exercises_json` read
 * as undefined rather than failing loudly. A malformed phase must be dropped
 * from the timer, never crash the deck on open.
 */
export function filterGhostPhases(phases: Phase[]): Phase[] {
  if (!Array.isArray(phases) || phases.length === 0) return [];
  const hasText = (v: unknown): boolean =>
    typeof v === 'string' ? v.trim().length > 0 : Array.isArray(v) && v.length > 0;
  const hasItems = (v: unknown): boolean => Array.isArray(v) && v.length > 0;
  // STRICT numeric — no JS truthy coercion. The backend runs a verbatim
  // port of this predicate (in_app_activity_bridge._filter_ghost_phases)
  // and the two index spaces MUST agree (Mission 35b MIRROR-DRIFT): the
  // old `p.duration_seconds && p.duration_seconds > 0` kept
  // `duration_seconds: true` and `"300"` via coercion while Python
  // dropped them — a one-phase index skew that misaligns every later
  // phase in the positional zip. Stringy durations are handled by
  // normalizePhaseDurations BEFORE this filter on the load path.
  const numPos = (v: unknown): boolean =>
    typeof v === 'number' && Number.isFinite(v) && v > 0;
  return phases.filter(p =>
    !!p && typeof p === 'object' && (
      numPos(p.duration_seconds) ||
      numPos(p.duration_minutes) ||
      numPos(p.distance_meters) ||
      numPos(p.distance_km) ||
      hasItems(p.exercises_json) ||
      hasText(p.instructions) ||
      hasItems(p.coaching_cues)
    )
  );
}

/**
 * Canonical per-phase duration, normalized ONCE at the load path (Mission
 * 35b WEDGE / Reconciliation 4). No normalization existed anywhere on the
 * timer's phase path — a plan-authored phase carrying only duration_minutes
 * reached the tick with duration_seconds undefined, where `n >= undefined`
 * is false forever: the engine parked permanently and a background return
 * minted NaN into phaseTimeRef. Returns a new object; never mutates.
 */
export function normalizePhaseDurations(phases: Phase[]): Phase[] {
  // Coerce a numeric STRING to its number ("300" arrives from hand-authored
  // and some LLM plan JSON). Anything else non-numeric resolves null —
  // never NaN, which is the value WEDGE exists to keep out of the engine.
  const asFiniteNumber = (v: unknown): number | null => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v === 'string' && v.trim() !== '') {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  };
  return phases.map((p) => {
    if (!p || typeof p !== 'object') return p;
    const secs = asFiniteNumber(p.duration_seconds);
    if (secs !== null && secs > 0) {
      // Already usable — but re-stamp when it arrived as a string so the
      // strict ghost filter (and the tick's Number.isFinite guard) see a
      // real number.
      return typeof p.duration_seconds === 'number' ? p : { ...p, duration_seconds: secs };
    }
    const mins = asFiniteNumber((p as any).duration_minutes);
    if (mins !== null && mins > 0) {
      return { ...p, duration_seconds: Math.round(mins * 60) };
    }
    return p;
  });
}

export function expandPhasesForTimer(phases: Phase[]): Phase[] {
  if (!Array.isArray(phases) || phases.length === 0) return [];

  // Ghost filtering moved to filterGhostPhases (exported, run unconditionally
  // by the load path). Kept here too so direct callers stay safe — the
  // filter is idempotent.
  const validPhases = filterGhostPhases(phases);

  const expanded: Phase[] = [];

  for (const phase of validPhases) {
    const count = phase.repeat_count ?? 0;

    if (count <= 1) {
      // Non-repeating phase — pass through as-is
      expanded.push(phase);
      continue;
    }

    // Repeating phase: unroll into N work phases with (N-1) recovery phases between
    const restSeconds = phase.rest_after_seconds ?? 0;

    const blockName = phase.phase_name || phase.name || 'Interval';

    for (let rep = 0; rep < count; rep++) {
      // Add the work phase (strip repeat_count so the timer doesn't try to re-expand)
      expanded.push({
        ...phase,
        repeat_count: 0,
        rest_after_seconds: 0,
        phase_name: `${blockName} ${rep + 1}/${count}`,
        expansion: { rep: rep + 1, repTotal: count, isRecovery: false, blockName },
      });

      // Add recovery phase between reps (not after the last one)
      if (rep < count - 1 && restSeconds > 0) {
        // 🆕 APR 2026: Use rich recovery fields from compact interval format
        // when available (e.g., "Walk Recovery" with coaching instructions)
        const recoveryName = phase.recovery_phase_name || 'Recovery';
        const recoveryInstructions = phase.recovery_instructions
          || `Recovery between reps — ${restSeconds}s`;
        const recoveryHrZone = phase.recovery_target_hr_zone ?? undefined;

        expanded.push({
          phase_type: 'recovery',
          phase_name: recoveryName,
          duration_seconds: restSeconds,
          duration_minutes: restSeconds / 60,
          target_hr_max: phase.recovery_hr_target ?? undefined,
          target_hr_zone: recoveryHrZone,
          instructions: recoveryInstructions,
          expansion: { rep: rep + 1, repTotal: count, isRecovery: true, blockName },
        });
      }
    }
  }

  // Re-number phase_order sequentially (expansion duplicates the original values)
  for (let i = 0; i < expanded.length; i++) {
    expanded[i] = { ...expanded[i], phase_order: i + 1 };
  }

  return expanded;
}
